import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Lyvya — cart drawer upsell card.
 *
 * Adds the upsell variant through the AJAX Cart API and dispatches the standard
 * `shopify:cart:lines-update` event (same contract as product-form.js), so
 * `<cart-items-component>` morphs the drawer with the returned section HTML,
 * the header cart bubble updates and no page reload happens.
 * The re-rendered drawer no longer contains the card (product now in the cart).
 */
class LyvyaCartUpsell extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-upsell-add]');
    this.errorEl = this.querySelector('[data-upsell-error]');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = () => {
    if (!(this.button instanceof HTMLButtonElement) || this.button.getAttribute('aria-busy') === 'true') return;
    this.#add();
  };

  async #add() {
    const button = /** @type {HTMLButtonElement} */ (this.button);
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (this.errorEl) this.errorEl.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const root = window.Theme?.routes?.root || '/';
    const addUrl = window.Theme?.routes?.cart_add_url || `${root.replace(/\/$/, '')}/cart/add.js`;
    const cartUrl = window.Theme?.routes?.cart_url || `${root.replace(/\/$/, '')}/cart`;

    try {
      const cfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(addUrl, { ...cfg, headers: { ...cfg.headers, Accept: 'application/json' } });
      const data = await response.json();
      const ajaxCart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (data.status) {
        this.#showError(data.description || data.message || 'Impossible d’ajouter ce produit.');
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'lyvya-cart-upsell' },
        });
        return;
      }

      const handle = this.dataset.productHandle;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          source: 'lyvya-cart-upsell',
          sections: data.sections,
        },
      });

      // The card disappears after the drawer morph: move focus to the new line item.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (this.isConnected && document.activeElement === button) return;
          const drawer = document.getElementById('cart-drawer');
          const target =
            (handle && drawer?.querySelector(`.cart-items__table-row a[href*="/products/${handle}"]`)) ||
            drawer?.querySelector('.theme-drawer__close-button');
          if (target instanceof HTMLElement) target.focus();
        })
      );
    } catch (error) {
      this.#showError('Une erreur est survenue. Veuillez réessayer.');
      deferred.reject(error);
    }
  }

  /** @param {string} message */
  #showError(message) {
    if (this.button instanceof HTMLButtonElement) {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    }
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }
}

if (!customElements.get('lyvya-cart-upsell')) {
  customElements.define('lyvya-cart-upsell', LyvyaCartUpsell);
}
